"use client";

import { useEffect, useRef } from "react";
import heroBuildings from "@/assets/hero_buildings_green_transparent.svg";

interface ServiceBackgroundProps {
  slug: string;
}

const backgroundConfig: Record<string, { elements: string[]; opacity: number; speed: number }> = {
  "web-development": {
    elements: ["browser", "code", "server", "database"],
    opacity: 0.15,
    speed: 0.3,
  },
  "mobile-development": {
    elements: ["phone", "tablet", "wifi", "notification"],
    opacity: 0.15,
    speed: 0.3,
  },
  "ui-ux-design": {
    elements: ["palette", "pen", "eye", "heart"],
    opacity: 0.15,
    speed: 0.3,
  },
  "cloud-devops": {
    elements: ["cloud", "gear", "arrow-up", "refresh"],
    opacity: 0.15,
    speed: 0.3,
  },
  "digital-transformation": {
    elements: ["zap", "trending-up", "lightbulb", "rocket"],
    opacity: 0.15,
    speed: 0.3,
  },
  cybersecurity: {
    elements: ["shield", "lock", "key", "eye"],
    opacity: 0.15,
    speed: 0.3,
  },
  // Industry slugs
  startup: {
    elements: ["rocket", "zap", "lightbulb", "trending-up"],
    opacity: 0.15,
    speed: 0.3,
  },
  umkm: {
    elements: ["store", "browser", "phone", "trending-up"],
    opacity: 0.15,
    speed: 0.3,
  },
  enterprise: {
    elements: ["building", "server", "gear", "shield"],
    opacity: 0.15,
    speed: 0.3,
  },
};

export default function ServiceBackground({ slug }: ServiceBackgroundProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animFrameRef = useRef<number>(0);
  const elementsRef = useRef<
    Array<{
      x: number;
      y: number;
      size: number;
      speed: number;
      opacity: number;
      type: string;
      rotation: number;
    }>
  >([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    // Load buildings image
    const buildingsImg = new Image();
    buildingsImg.src = heroBuildings;

    const config = backgroundConfig[slug] || backgroundConfig["web-development"];

    // Generate floating elements - larger and more visible
    elementsRef.current = Array.from({ length: 25 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      size: Math.random() * 50 + 30,
      speed: Math.random() * config.speed + 0.1,
      opacity: Math.random() * config.opacity + 0.05,
      type: config.elements[Math.floor(Math.random() * config.elements.length)],
      rotation: Math.random() * 360,
    }));

    const drawIcon = (
      type: string,
      x: number,
      y: number,
      size: number,
      opacity: number,
      rotation: number
    ) => {
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.globalAlpha = opacity;
      ctx.strokeStyle = "rgba(0, 220, 130, 1)";
      ctx.lineWidth = 4;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";

      switch (type) {
        case "browser":
          ctx.beginPath();
          ctx.rect(-size / 2, -size / 3, size, (size * 2) / 3);
          ctx.moveTo(-size / 2, -size / 6);
          ctx.lineTo(size / 2, -size / 6);
          ctx.moveTo(0, -size / 6);
          ctx.lineTo(0, size / 6);
          ctx.stroke();
          break;

        case "code":
          ctx.beginPath();
          ctx.moveTo(-size / 4, -size / 4);
          ctx.lineTo(-size / 2, 0);
          ctx.lineTo(-size / 4, size / 4);
          ctx.moveTo(size / 4, -size / 4);
          ctx.lineTo(size / 2, 0);
          ctx.lineTo(size / 4, size / 4);
          ctx.moveTo(size / 6, -size / 3);
          ctx.lineTo(-size / 6, size / 3);
          ctx.stroke();
          break;

        case "server":
          ctx.beginPath();
          for (let i = 0; i < 3; i++) {
            const yOffset = -size / 3 + (i * size) / 3;
            ctx.rect(-size / 2, yOffset, size, size / 4);
            ctx.moveTo(-size / 3, yOffset + size / 8);
            ctx.lineTo(-size / 3 + size / 6, yOffset + size / 8);
          }
          ctx.stroke();
          break;

        case "database":
          ctx.beginPath();
          ctx.ellipse(0, -size / 4, size / 2, size / 6, 0, 0, Math.PI * 2);
          ctx.moveTo(-size / 2, -size / 4);
          ctx.lineTo(-size / 2, size / 4);
          ctx.ellipse(0, size / 4, size / 2, size / 6, 0, 0, Math.PI);
          ctx.moveTo(-size / 2, size / 4);
          ctx.lineTo(-size / 2, -size / 4);
          ctx.stroke();
          break;

        case "phone":
          ctx.beginPath();
          ctx.rect(-size / 3, -size / 2, (size * 2) / 3, size);
          ctx.moveTo(0, size / 2 - size / 6);
          ctx.arc(0, size / 2 - size / 6, size / 8, 0, Math.PI * 2);
          ctx.stroke();
          break;

        case "tablet":
          ctx.beginPath();
          ctx.rect(-size / 2, -size / 1.5, size, size * 1.3);
          ctx.moveTo(-size / 3, -size / 4);
          ctx.lineTo(size / 3, -size / 4);
          ctx.stroke();
          break;

        case "wifi":
          for (let i = 0; i < 3; i++) {
            const radius = size / 6 + (i * size) / 6;
            ctx.beginPath();
            ctx.arc(0, size / 4, radius, -Math.PI * 0.8, -Math.PI * 0.2);
            ctx.stroke();
          }
          ctx.beginPath();
          ctx.arc(0, size / 4, size / 12, 0, Math.PI * 2);
          ctx.fill();
          break;

        case "notification":
          ctx.beginPath();
          ctx.moveTo(0, -size / 2);
          ctx.quadraticCurveTo(size / 2, -size / 3, size / 2, 0);
          ctx.lineTo(size / 2, size / 4);
          ctx.quadraticCurveTo(size / 2, size / 2, 0, size / 2);
          ctx.quadraticCurveTo(-size / 2, size / 2, -size / 2, size / 4);
          ctx.lineTo(-size / 2, 0);
          ctx.quadraticCurveTo(-size / 2, -size / 3, 0, -size / 2);
          ctx.stroke();
          ctx.beginPath();
          ctx.arc(0, size / 2, size / 8, 0, Math.PI * 2);
          ctx.fill();
          break;

        case "palette":
          ctx.beginPath();
          ctx.ellipse(0, 0, size / 2, size / 3, 0, 0, Math.PI * 2);
          ctx.stroke();
          ctx.beginPath();
          ctx.arc(-size / 4, -size / 6, size / 10, 0, Math.PI * 2);
          ctx.arc(size / 4, -size / 6, size / 10, 0, Math.PI * 2);
          ctx.arc(0, size / 6, size / 10, 0, Math.PI * 2);
          ctx.fill();
          break;

        case "pen":
          ctx.beginPath();
          ctx.moveTo(-size / 4, -size / 2);
          ctx.lineTo(size / 4, -size / 2);
          ctx.lineTo(size / 4, size / 3);
          ctx.lineTo(0, size / 2);
          ctx.lineTo(-size / 4, size / 3);
          ctx.closePath();
          ctx.stroke();
          break;

        case "eye":
          ctx.beginPath();
          ctx.moveTo(-size / 2, 0);
          ctx.quadraticCurveTo(0, -size / 2, size / 2, 0);
          ctx.quadraticCurveTo(0, size / 2, -size / 2, 0);
          ctx.stroke();
          ctx.beginPath();
          ctx.arc(0, 0, size / 6, 0, Math.PI * 2);
          ctx.fill();
          break;

        case "heart":
          ctx.beginPath();
          ctx.moveTo(0, size / 3);
          ctx.quadraticCurveTo(-size / 2, -size / 6, -size / 4, -size / 3);
          ctx.quadraticCurveTo(0, -size / 4, 0, -size / 6);
          ctx.quadraticCurveTo(0, -size / 4, size / 4, -size / 3);
          ctx.quadraticCurveTo(size / 2, -size / 6, 0, size / 3);
          ctx.stroke();
          break;

        case "cloud":
          ctx.beginPath();
          ctx.arc(-size / 6, 0, size / 4, 0, Math.PI * 2);
          ctx.arc(size / 6, 0, size / 4, 0, Math.PI * 2);
          ctx.arc(0, -size / 6, size / 4, 0, Math.PI * 2);
          ctx.rect(-size / 2, 0, size, size / 4);
          ctx.fill();
          break;

        case "gear":
          const teeth = 8;
          const outerRadius = size / 2;
          const innerRadius = size / 3;
          ctx.beginPath();
          for (let i = 0; i < teeth * 2; i++) {
            const angle = (i * Math.PI) / teeth;
            const radius = i % 2 === 0 ? outerRadius : innerRadius;
            ctx.lineTo(Math.cos(angle) * radius, Math.sin(angle) * radius);
          }
          ctx.closePath();
          ctx.stroke();
          ctx.beginPath();
          ctx.arc(0, 0, size / 6, 0, Math.PI * 2);
          ctx.stroke();
          break;

        case "arrow-up":
          ctx.beginPath();
          ctx.moveTo(0, size / 2);
          ctx.lineTo(0, -size / 2);
          ctx.moveTo(-size / 4, -size / 6);
          ctx.lineTo(0, -size / 2);
          ctx.lineTo(size / 4, -size / 6);
          ctx.stroke();
          break;

        case "refresh":
          ctx.beginPath();
          ctx.arc(0, 0, size / 2, -Math.PI / 2, Math.PI);
          ctx.moveTo(-size / 2, 0);
          ctx.lineTo(-size / 2 - size / 6, size / 6);
          ctx.lineTo(-size / 2 + size / 6, size / 6);
          ctx.closePath();
          ctx.stroke();
          break;

        case "zap":
          ctx.beginPath();
          ctx.moveTo(size / 6, -size / 2);
          ctx.lineTo(-size / 3, 0);
          ctx.lineTo(-size / 6, 0);
          ctx.lineTo(-size / 6, size / 2);
          ctx.lineTo(size / 3, 0);
          ctx.lineTo(size / 6, 0);
          ctx.closePath();
          ctx.fill();
          break;

        case "trending-up":
          ctx.beginPath();
          ctx.moveTo(-size / 2, size / 3);
          ctx.lineTo(-size / 6, 0);
          ctx.lineTo(size / 6, size / 6);
          ctx.lineTo(size / 2, -size / 3);
          ctx.moveTo(size / 2 - size / 6, -size / 3);
          ctx.lineTo(size / 2, -size / 3);
          ctx.lineTo(size / 2, -size / 3 + size / 6);
          ctx.stroke();
          break;

        case "lightbulb":
          ctx.beginPath();
          ctx.arc(0, -size / 6, size / 3, -Math.PI * 0.8, Math.PI * 0.8);
          ctx.lineTo(-size / 5, size / 4);
          ctx.lineTo(size / 5, size / 4);
          ctx.closePath();
          ctx.stroke();
          ctx.beginPath();
          ctx.moveTo(-size / 6, size / 2);
          ctx.lineTo(size / 6, size / 2);
          ctx.moveTo(-size / 5, size / 2 + size / 6);
          ctx.lineTo(size / 5, size / 2 + size / 6);
          ctx.stroke();
          break;

        case "rocket":
          ctx.beginPath();
          ctx.moveTo(0, -size / 2);
          ctx.quadraticCurveTo(size / 3, -size / 4, size / 3, size / 6);
          ctx.lineTo(size / 4, size / 3);
          ctx.lineTo(0, size / 4);
          ctx.lineTo(-size / 4, size / 3);
          ctx.lineTo(-size / 3, size / 6);
          ctx.quadraticCurveTo(-size / 3, -size / 4, 0, -size / 2);
          ctx.stroke();
          ctx.beginPath();
          ctx.arc(0, -size / 6, size / 8, 0, Math.PI * 2);
          ctx.stroke();
          break;

        case "shield":
          ctx.beginPath();
          ctx.moveTo(0, -size / 2);
          ctx.quadraticCurveTo(size / 2, -size / 3, size / 2, 0);
          ctx.quadraticCurveTo(size / 2, size / 2, 0, size / 2);
          ctx.quadraticCurveTo(-size / 2, size / 2, -size / 2, 0);
          ctx.quadraticCurveTo(-size / 2, -size / 3, 0, -size / 2);
          ctx.stroke();
          break;

        case "lock":
          ctx.beginPath();
          ctx.rect(-size / 3, -size / 6, (size * 2) / 3, size / 2);
          ctx.moveTo(-size / 4, -size / 6);
          ctx.lineTo(-size / 4, -size / 3);
          ctx.arc(0, -size / 3, size / 4, Math.PI, 0);
          ctx.lineTo(size / 4, -size / 6);
          ctx.stroke();
          ctx.beginPath();
          ctx.arc(0, size / 6, size / 8, 0, Math.PI * 2);
          ctx.fill();
          break;

        case "key":
          ctx.beginPath();
          ctx.arc(-size / 4, 0, size / 4, 0, Math.PI * 2);
          ctx.moveTo(0, 0);
          ctx.lineTo(size / 2, 0);
          ctx.moveTo(size / 4, 0);
          ctx.lineTo(size / 4, size / 6);
          ctx.moveTo(size / 6, 0);
          ctx.lineTo(size / 6, size / 6);
          ctx.stroke();
          ctx.beginPath();
          ctx.arc(-size / 4, 0, size / 8, 0, Math.PI * 2);
          ctx.fill();
          break;

        default:
          ctx.beginPath();
          ctx.arc(0, 0, size / 4, 0, Math.PI * 2);
          ctx.stroke();
      }

      ctx.restore();
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw buildings background (tiled)
      if (buildingsImg.complete && buildingsImg.naturalWidth > 0) {
        ctx.globalAlpha = 0.12;
        const imgRatio = buildingsImg.naturalWidth / buildingsImg.naturalHeight;
        const canvasRatio = canvas.width / canvas.height;
        let drawWidth, drawHeight, offsetX, offsetY;

        if (canvasRatio > imgRatio) {
          drawWidth = canvas.width;
          drawHeight = canvas.width / imgRatio;
          offsetX = 0;
          offsetY = (canvas.height - drawHeight) / 2;
        } else {
          drawHeight = canvas.height;
          drawWidth = canvas.height * imgRatio;
          offsetX = (canvas.width - drawWidth) / 2;
          offsetY = 0;
        }

        ctx.drawImage(buildingsImg, offsetX, offsetY, drawWidth, drawHeight);
        ctx.globalAlpha = 1;
      }

      // Draw floating icons on top
      elementsRef.current.forEach((el) => {
        el.y -= el.speed;
        el.rotation += 0.2;

        if (el.y < -el.size) {
          el.y = canvas.height + el.size;
          el.x = Math.random() * canvas.width;
        }

        drawIcon(el.type, el.x, el.y, el.size, el.opacity, el.rotation);
      });

      animFrameRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      cancelAnimationFrame(animFrameRef.current);
    };
  }, [slug]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ zIndex: 1 }}
    />
  );
}
